import type { DateRange } from 'react-day-picker'
import type { WidgetPeriod } from '../widgets/types'

import { useCallback, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
	createStaticRange,
	DateRangeMenuPanel,
	defaultPresets,
	useRangePreset,
} from '@/shared/components/ui/date-picker-ranges'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { DateFormat, getUTCDate, intlFormat } from '@/shared/tools/dates'

import { getLocalization } from './localization'

type SinglePeriodsSelectProps = {
	period: WidgetPeriod
	onChangePeriod: (period: WidgetPeriod) => void
	defaultPeriod: WidgetPeriod
	maxDate?: Date
}
const toRangeValues = (value: {
	start_date: string
	end_date: string
}): DateRange => ({
	from: getUTCDate(new Date(value.start_date)),
	to: getUTCDate(new Date(value.end_date)),
})
export default function SinglePeriodSelect({
	period,
	onChangePeriod,
	maxDate,
	defaultPeriod,
}: SinglePeriodsSelectProps) {
	const value = useMemo(() => toRangeValues(period), [period])
	const setValue = useCallback(
		(range: DateRange) =>
			onChangePeriod({
				start_date: format(new Date(range.from as Date), DateFormat.system),
				end_date: format(new Date(range.to as Date), DateFormat.system),
			}),
		[]
	)

	return (
		<PeriodPicker
			value={value}
			defaultValue={toRangeValues(defaultPeriod)}
			setValue={setValue}
			maxDate={maxDate!}
		/>
	)
}

function PeriodPicker({
	value,
	setValue,
	defaultValue,
	maxDate,
}: {
	value: DateRange
	setValue: (value: DateRange) => void
	defaultValue: DateRange
	maxDate?: Date
}) {
	const { t } = useTranslation()

	const [open, setOpen] = useState(false)
	const staticRanges = createStaticRange(defaultPresets)
	const selected = useRangePreset({
		value,
		staticRanges,
	})
	const selectedLabel = useMemo(() => {
		if (!selected) return null
		if (selected.label) return selected.label

		return (
			intlFormat(selected.range.from!) + ' - ' + intlFormat(selected.range.to!)
		)
	}, [selected])

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button variant='ghost'>
					<CalendarIcon />
					{selectedLabel ||
						t('calendar.input.select_dates', { defaultValue: 'Select dates' })}
				</Button>
			</PopoverTrigger>
			<PopoverContent className='w-auto p-0'>
				<DateRangeMenuPanel
					localization={getLocalization(t)}
					value={value}
					maxDate={maxDate}
					defaultValue={defaultValue}
					defaultPresets={staticRanges}
					onValueChange={(range) => {
						setValue(range)
						setOpen(false)
					}}
				/>
			</PopoverContent>
		</Popover>
	)
}
